import { get } from 'lodash'
import { formatContractDurationBetweenDates } from '../../../utils/containerOfferHelperFunctions'
import {
  AgreementBasedOffer,
  SubscriptionDetailObject,
} from '../../../modules/subscription/reducer'
import type {
  ChannelPartnerPrivateOffer,
  Installments,
} from '../../../types/flyout'
import { normalizeAwsRenewalTermsFromApi } from '../../../modules/privateOffer/privateOfferCreation/utils/installmentHelper'
import type { RenewalDetailsLike } from '../../../common/utils/renewalDetailsFormat'

type RenewalInstallmentApiRow = {
  paymentDate?: string
  amount?: number | null
  currency?: string
  monthsAfter?: number
  monthsAfterContractStart?: number
  dayOfMonth?: number
  percentage?: number
  percentageOfTcv?: number
}

type RenewalInstallmentsSource = {
  renewalInstallments?: RenewalInstallmentApiRow[]
  renewalPricingConfig?: { renewalInstallments?: RenewalInstallmentApiRow[] }
}

export const getSubscriptionContractStartDate = (
  subscriptionDetail: SubscriptionDetailObject
): string | undefined => {
  const subscription = subscriptionDetail?.subscription
  const offerDetails = subscriptionDetail?.offerDetails

  if (offerDetails?.agreementStartDate) {
    return offerDetails.agreementStartDate
  }

  return (
    subscription?.contractStartDate ||
    offerDetails?.acceptedOn ||
    subscription?.createdAt ||
    undefined
  )
}

export const getContractDuration = (
  subscription: SubscriptionDetailObject['subscription'],
  contractStartDate?: string
) => {
  const startDateValue = contractStartDate || subscription?.createdAt

  if (startDateValue && subscription?.expirationDate) {
    return formatContractDurationBetweenDates(
      startDateValue,
      subscription.expirationDate,
      { endDate: 'monthAnniversary' }
    )
  }
  return '-'
}

export const getOfferDetailInfo = ({
  subscriptionDetail,
  isCPPOSubscription,
}: {
  subscriptionDetail: SubscriptionDetailObject
  isCPPOSubscription: boolean
}) => {
  const offerData = get(
    subscriptionDetail,
    `${isCPPOSubscription ? 'cppoDetails' : 'offerDetails'}`
  )
  return {
    offerName: isCPPOSubscription
      ? (offerData as ChannelPartnerPrivateOffer)?.offerName
      : (offerData as AgreementBasedOffer)?.privateOfferName,

    createdAt: isCPPOSubscription
      ? (offerData as ChannelPartnerPrivateOffer)?.createdAt
      : (offerData as AgreementBasedOffer)?.createdAt,

    labraOfferId: isCPPOSubscription
      ? (offerData as ChannelPartnerPrivateOffer)?.labraOfferId
      : (offerData as AgreementBasedOffer)?.labraOfferId,

    cloudOfferId: isCPPOSubscription
      ? (offerData as ChannelPartnerPrivateOffer)?.cloudOfferId
      : (offerData as AgreementBasedOffer)?.cloudOfferId,

    eulaType: isCPPOSubscription
      ? (offerData as ChannelPartnerPrivateOffer)?.eulaType
      : (offerData as AgreementBasedOffer)?.eulaType,

    companyName: isCPPOSubscription
      ? (offerData as ChannelPartnerPrivateOffer)?.companyName
      : subscriptionDetail?.buyerDetails?.companyName,
  }
}

export const getSubscriptionRenewalInstallments = (
  detail: SubscriptionDetailObject
): Installments[] => {
  const subscription =
    detail.subscription as SubscriptionDetailObject['subscription'] &
      RenewalInstallmentsSource
  const offer = detail.offerDetails as
    | (AgreementBasedOffer & RenewalInstallmentsSource)
    | undefined
  const raw =
    detail.renewalInstallments ||
    subscription?.renewalInstallments ||
    offer?.renewalInstallments ||
    subscription?.renewalPricingConfig?.renewalInstallments ||
    offer?.renewalPricingConfig?.renewalInstallments ||
    []

  return (raw as RenewalInstallmentApiRow[]).map(row => ({
    paymentDate: row.paymentDate || '',
    amount: row.amount ?? null,
    currency: row.currency || subscription?.currency || 'USD',
    monthsAfter: row.monthsAfter ?? row.monthsAfterContractStart,
    dayOfMonth: row.dayOfMonth,
    percentage: row.percentage ?? row.percentageOfTcv,
  }))
}

/** Flatten offer + subscription API shapes onto the fields the renewal UI reads. */
export const getSubscriptionRenewalDetailsItem = (
  detail: SubscriptionDetailObject
): RenewalDetailsLike => {
  const subscription = (detail?.subscription || {}) as unknown as Record<
    string,
    unknown
  >
  const offer = (detail?.offerDetails || {}) as unknown as Record<
    string,
    unknown
  >
  const cfg = subscription.renewalPricingConfig || offer.renewalPricingConfig
  return normalizeAwsRenewalTermsFromApi({
    ...offer,
    ...subscription,
    ...(cfg ? { renewalPricingConfig: cfg } : {}),
    renewalPricingTerm:
      subscription.renewalPricingTerm ??
      offer.renewalPricingTerm ??
      subscription.renewalPricingTerms ??
      offer.renewalPricingTerms,
  }) as RenewalDetailsLike
}
